import { shallowReactive, type App } from 'vue';
import type { RouteLocationNormalized, RouteRecordRaw, Router } from 'vue-router';
import type { ConnectAppModule } from '@blpde/connect';
import { apps, type AppEntry, type AppId } from '@/integration';
import type { NavSpec } from '@/integration/nav';
import { withAppI18nRoute } from '@/i18n/appI18n';

/**
 * Hosted apps load on demand (I-1786). Until an app is opened, the router only
 * knows a placeholder below the app's base path. The first navigation there loads
 * the app's module — its own bundle in production — mounts its routes and runs the
 * navigation again against them.
 *
 * The shell has one router, so loaded modules are kept per app, not per router.
 */

const loaded = shallowReactive(new Map<AppId, ConnectAppModule>());
const loading = new Map<AppId, Promise<ConnectAppModule>>();
let vueApp: App | null = null;

/** The Vue app that app modules install into; set before the router starts. */
export function setConnectVueApp(app: App): void {
  vueApp = app;
}

const placeholderName = (id: AppId) => `${id}.__load`;

/** Catch-all below an app's base path that stands in until the app is loaded. */
export function appPlaceholderRoute(app: AppEntry): RouteRecordRaw {
  return {
    path: `${app.basePath}/:connectAppPath(.*)*`,
    name: placeholderName(app.id),
    component: { render: () => null },
    meta: { connectApp: app.id },
  };
}

/** An app's loaded module (reactive); undefined until the app was opened. */
export function loadedApp(id: AppId): ConnectAppModule | undefined {
  return loaded.get(id);
}

/** The navbar of an app: its loaded module's own, else the shell's static entry. */
export function appNav(app: AppEntry | undefined): NavSpec | undefined {
  if (!app) return undefined;
  return loaded.get(app.id)?.nav ?? app.nav;
}

/** Loads an app's module once and mounts its routes; concurrent callers share one load. */
export function installConnectApp(router: Router, id: AppId): Promise<ConnectAppModule> {
  const module = loaded.get(id);
  if (module) return Promise.resolve(module);
  const inFlight = loading.get(id);
  if (inFlight) return inFlight;

  const app = apps.find((entry) => entry.id === id);
  if (!app) return Promise.reject(new Error(`Unknown Connect app "${id}"`));
  const install = app
    .load()
    .then(async ({ default: appModule }) => {
      if (vueApp) await appModule.install?.(vueApp);
      // The app's pages translate with its own table, never another app's (I-2284).
      router.addRoute(withAppI18nRoute(id, appModule.route));
      if (router.hasRoute(placeholderName(id))) router.removeRoute(placeholderName(id));
      loaded.set(id, appModule);
      return appModule;
    })
    // A failed load keeps the placeholder, so the next navigation tries again.
    .finally(() => loading.delete(id));
  loading.set(id, install);
  return install;
}

/**
 * First navigation into an app: load it, then navigate again against its own routes.
 * An app with `loadNeedsSession` is loaded only when `hasSession()` says the user is
 * signed in; otherwise the navigation passes on to the auth gate, which redirects it
 * to the login (the placeholder is not a public route).
 *
 * The user may go elsewhere while the app loads. vue-router notices that only after
 * all its guards, and a redirect from a guard skips that check: running this one's
 * would bring the user back into the app. So the overtaken navigation ends here.
 */
export function installAppLoaderGuard(
  router: Router,
  hasSession: () => Promise<boolean> = async () => true
): void {
  let latest: RouteLocationNormalized | null = null;
  router.beforeEach(async (to) => {
    latest = to;
    const id = to.meta.connectApp as AppId | undefined;
    if (!id) return true;
    if (apps.find((entry) => entry.id === id)?.loadNeedsSession && !(await hasSession())) {
      return true;
    }
    await installConnectApp(router, id);
    return latest === to ? to.fullPath : false;
  });
}
